import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { AttendanceChip } from './StatusChip';
import api from '../services/api';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';

const PREVIEW = 5;

// Los dos tipos que se registran a mano. `training` es la capacitación inicial
// —la única que cuenta para el porcentaje de capacitación— y `support` son las
// sesiones de dudas. Los demás tipos que admite la tabla (qbr, check_in,
// onboarding) llegan desde el calendario, no desde este formulario.
const MANUAL_KINDS = ['training', 'support'];

// Una fecha suelta se interpreta en UTC: new Date('2026-09-03') es el 2 de
// septiembre a las 18:00 en México, y la sesión aparecería un día antes.
// Mediodía local evita el corrimiento en cualquier huso.
function atLocalNoon(value) {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

const todayValue = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// Bitácora de reuniones de la cuenta: qué se dio, cuándo y si llegaron.
// El registro a mano existe para lo que el calendario no puede ver — una
// capacitación fuera de Meet, o una reunión que nunca se ligó a la cuenta.
export default function AccountSessions({ tenantId, source, onChanged }) {
  const { t, fmtDate, tApiError } = useI18n();
  const canLog = useCan('log_activity');

  const [sessions, setSessions] = useState([]);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Nota que se está editando: { id, text }. Lo normal es que se escriba
  // después de la sesión, no al registrarla.
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get('/api/sessions', { params: { tenantId, source } })
      // Más reciente primero: el endpoint las ordena ascendente para el calendario.
      .then((res) => setSessions([...res.data].reverse()))
      .catch(() => setFailed(true));
  }, [tenantId, source]);

  useEffect(() => { load(); }, [load]);

  async function create() {
    if (!draft?.date) return;
    const when = atLocalNoon(draft.date);
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/sessions', {
        tenant_id: tenantId,
        source_db: source,
        kind: draft.kind,
        title: draft.title?.trim() || null,
        notes: draft.notes?.trim() || null,
        scheduled_at: when.toISOString(),
        // Registrar algo que ya pasó es afirmar que ocurrió: si se deja sin
        // asistencia no cuenta para el porcentaje y además engrosa el contador
        // de «sin respuesta». Una sesión futura sí queda pendiente de responder.
        attendance: when < new Date() ? 'attended' : null,
      });
      setDraft(null);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(tApiError(err, 'sessionsLog.saveError'));
    } finally {
      setBusy(false);
    }
  }

  async function saveNotes() {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      // Cadena vacía, no null: es lo que el backend interpreta como «bórrala».
      await api.patch(`/api/sessions/${editing.id}`, { notes: editing.text.trim() });
      setEditing(null);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(tApiError(err, 'sessionsLog.saveError'));
    } finally {
      setBusy(false);
    }
  }

  const shown = expanded ? sessions : sessions.slice(0, PREVIEW);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
        <Typography sx={{
          fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#847782',
        }}>{t('sessionsLog.title')}</Typography>
        <Typography sx={{ fontSize: 13, color: '#FF6666', fontWeight: 600 }}>
          {sessions.length}
        </Typography>
        {canLog && !draft && (
          <Button
            size="small"
            onClick={() => setDraft({ title: '', date: todayValue(), kind: 'training' })}
            sx={{ ml: 'auto', fontSize: 11.5, color: '#FF6666' }}
          >{t('sessionsLog.add')}</Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {draft && (
        <Box sx={{
          border: '1px solid #E4E0E2', borderRadius: '5px', p: 1.5, mb: 1.5,
          backgroundColor: '#F5F5F5',
        }}>
          <TextField
            size="small" fullWidth variant="standard" autoFocus
            placeholder={t('sessionsLog.titlePlaceholder')}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            sx={{ mb: 1.5, '& input': { fontSize: 13.5, fontFamily: 'Saira' } }}
          />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              type="date" size="small" variant="standard"
              label={t('sessionsLog.date')}
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              sx={{ '& input': { fontSize: 13.5, fontFamily: 'Saira' } }}
            />
            <TextField
              select size="small" variant="standard"
              label={t('sessionsLog.kind')}
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              sx={{ minWidth: 150, '& .MuiSelect-select': { fontSize: 13.5, fontFamily: 'Saira' } }}
            >
              {MANUAL_KINDS.map((kind) => (
                <MenuItem key={kind} value={kind} sx={{ fontSize: 13.5 }}>
                  {t(`kinds.${kind}`)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            size="small" fullWidth multiline minRows={2}
            placeholder={t('sessionsLog.notesPlaceholder')}
            value={draft.notes || ''}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            sx={{ mt: 1.5, '& textarea': { fontSize: 13, fontFamily: 'Saira' } }}
          />

          <Typography sx={{ fontSize: 11, color: '#847782', mt: 1.2, lineHeight: 1.45 }}>
            {draft.kind === 'training'
              ? t('sessionsLog.countsHint')
              : t('sessionsLog.noCountHint')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              size="small" variant="contained" disabled={busy || !draft.date}
              onClick={create} sx={{ fontSize: 12 }}
            >{t('common.save')}</Button>
            <Button
              size="small" disabled={busy} onClick={() => { setDraft(null); setError(null); }}
              sx={{ fontSize: 12, color: '#847782' }}
            >{t('sessionsLog.cancel')}</Button>
          </Box>
        </Box>
      )}

      {failed && (
        <Typography sx={{ fontSize: 13, color: '#AE5B61' }}>{t('sessionsLog.loadError')}</Typography>
      )}
      {!failed && sessions.length === 0 && !draft && (
        <Typography sx={{ fontSize: 13.5, color: '#847782' }}>{t('sessionsLog.empty')}</Typography>
      )}

      {shown.map((s) => (
        <Box
          key={s.id}
          sx={{
            py: 0.9,
            borderBottom: '1px solid #F0ECEE', '&:last-of-type': { borderBottom: 'none' },
          }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
              {s.title || t(`kinds.${s.kind}`)}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
              {fmtDate(s.scheduled_at)}
              {s.title && ` · ${t(`kinds.${s.kind}`)}`}
              {/* Invitados contra asistentes es la lectura útil; el conteo a
                  secas no dice si la sala se llenó o se vació. */}
              {s.invited_count
                ? ` · ${t('sessionsLog.ofInvited', {
                    attended: s.attendee_count ?? 0, invited: s.invited_count,
                  })}`
                : (s.attendee_count
                    ? ` · ${t('sessionsLog.attendees', { count: s.attendee_count })}`
                    : '')}
            </Typography>
          </Box>
          <AttendanceChip attendance={s.attendance} />
        </Box>

        {/* Lo que se vio en la sesión. Casi siempre se escribe después, así que
            se edita desde el propio renglón en vez de obligar a un formulario. */}
        {editing?.id === s.id ? (
          <Box sx={{ mt: 0.8 }}>
            <TextField
              size="small" fullWidth multiline minRows={2} autoFocus
              placeholder={t('sessionsLog.notesPlaceholder')}
              value={editing.text}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
              sx={{ '& textarea': { fontSize: 13, fontFamily: 'Saira' } }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.8 }}>
              <Button size="small" variant="contained" disabled={busy}
                onClick={saveNotes} sx={{ fontSize: 11.5 }}>{t('common.save')}</Button>
              <Button size="small" disabled={busy} onClick={() => setEditing(null)}
                sx={{ fontSize: 11.5, color: '#847782' }}>{t('sessionsLog.cancel')}</Button>
            </Box>
          </Box>
        ) : s.notes ? (
          <Typography
            onClick={() => canLog && setEditing({ id: s.id, text: s.notes })}
            sx={{
              fontSize: 12.5, color: '#170312', mt: 0.5, whiteSpace: 'pre-wrap',
              borderLeft: '2px solid #E4E0E2', pl: 1.2, lineHeight: 1.45,
              cursor: canLog ? 'pointer' : 'default',
            }}
          >{s.notes}</Typography>
        ) : canLog ? (
          <Typography
            onClick={() => setEditing({ id: s.id, text: '' })}
            sx={{ fontSize: 11.5, color: '#FF6666', mt: 0.3, cursor: 'pointer' }}
          >{t('sessionsLog.addNotes')}</Typography>
        ) : null}
        </Box>
      ))}

      {sessions.length > PREVIEW && (
        <Typography
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: 12, color: '#531253', textAlign: 'center', mt: 1.2,
            cursor: 'pointer', fontWeight: 500,
          }}
        >
          {expanded ? t('sessionsLog.showLess') : t('sessionsLog.showAll', { count: sessions.length })}
        </Typography>
      )}
    </>
  );
}
